import { create } from 'zustand'
import { supabase } from '@/lib/supabase'
import type { Supply, SupplyFormData } from '@/types'

interface SupplyState {
  supplies: Supply[]
  loading: boolean
  error: string | null
  fetchSupplies: () => Promise<void>
  addSupply: (data: SupplyFormData) => Promise<Supply | null>
  updateSupply: (id: string, data: Partial<SupplyFormData>) => Promise<void>
  deleteSupply: (id: string) => Promise<void>
  getLowInventory: () => Supply[]
}

export const useSupplyStore = create<SupplyState>((set, get) => ({
  supplies: [],
  loading: false,
  error: null,

  fetchSupplies: async () => {
    set({ loading: true, error: null })
    try {
      const { data, error } = await supabase
        .from('supplies')
        .select('*')
        .order('name', { ascending: true })

      if (error) throw error
      set({ supplies: data || [], loading: false })
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  addSupply: async (data: SupplyFormData) => {
    set({ loading: true, error: null })
    try {
      const { data: newSupply, error } = await supabase
        .from('supplies')
        // @ts-ignore - Supabase type inference issue with Insert types
        .insert([data])
        .select()
        .single()

      if (error) throw error

      set((state) => ({
        supplies: [...state.supplies, newSupply],
        loading: false,
      }))

      return newSupply
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
      return null
    }
  },

  updateSupply: async (id: string, data: Partial<SupplyFormData>) => {
    set({ loading: true, error: null })
    try {
      const { error } = await supabase
        .from('supplies')
        // @ts-ignore - Supabase type inference issue with Update types
        .update(data)
        .eq('id', id)

      if (error) throw error

      set((state) => ({
        supplies: state.supplies.map((s) =>
          s.id === id ? { ...s, ...data, updated_at: new Date().toISOString() } : s
        ),
        loading: false,
      }))
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  deleteSupply: async (id: string) => {
    set({ loading: true, error: null })
    try {
      const { error } = await supabase
        .from('supplies')
        .delete()
        .eq('id', id)

      if (error) throw error

      set((state) => ({
        supplies: state.supplies.filter((s) => s.id !== id),
        loading: false,
      }))
    } catch (error) {
      set({ error: (error as Error).message, loading: false })
    }
  },

  getLowInventory: () => {
    return get().supplies.filter((s) => s.quantity <= s.min_quantity)
  },
}))
